import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  TransactionListQuery,
  TransactionRepository,
} from '@app/domain/ports/tpv/transaction.repository';
import { TpvTransaction } from '@app/domain/models/tpv/transaction.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { TransactionDto } from '@app/data/dtos/tpv/transaction.dto';
import { mapTransaction } from '@app/data/mappers/tpv/transaction.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** `GET /api/v1/tpv/transactions/`. Auth vía interceptores estándar. */
@Injectable()
export class TransactionRemoteDataSource extends TransactionRepository {
  private readonly http = inject(HttpClient);

  override getTransactions(
    externalIdMerchant?: string,
    query: TransactionListQuery = {},
  ): Observable<TpvPage<TpvTransaction>> {
    const page = query.page ?? 1;
    const limit = query.limit ?? 10;
    // Multi-valor: el contrato acepta coma o param repetido; usamos CSV (más compacto).
    // Las KEYS llegan ya en MAYÚSCULAS desde la capa de presentación.
    const csv = (v?: string[]): string | undefined =>
      v && v.length ? v.join(',') : undefined;
    // Sin `id_merchant` → el backend acota a los comercios del usuario (toHttpParams
    // omite el valor undefined, así que el param simplemente no se envía).
    const params = toHttpParams({
      id_merchant: externalIdMerchant,
      page,
      limit,
      start_date: query.startDate,
      end_date: query.endDate,
      // Caja libre: el componente manda el mismo término a los tres params y
      // el micro hace OR server-side (contrato suite #278 + micro #42, 2026-10-05).
      merchant_name: query.merchantName,
      company_name: query.companyName,
      unit_name: query.unitName,
      status: csv(query.status),
      payment_method: csv(query.paymentMethod),
      card_brand: csv(query.cardBrand),
      operation_type: csv(query.operationType),
      processor: csv(query.processor),
      id_terminal: csv(query.idTerminal),
      min_amount: query.minAmount,
      max_amount: query.maxAmount,
      approved: query.approved,
      // Un solo valor (no CSV): el contrato no documentó multi-valor para el correo.
      email: query.email,
    });

    return this.http
      .get<TpvEnvelope<unknown>>(tpvUrl('transactions/'), { params })
      .pipe(
        map((raw) =>
          toTpvPage<TransactionDto, TpvTransaction>(raw, mapTransaction, { page, limit }),
        ),
      );
  }
}
